/**
 * Moving what the app knows about a car -- vehicle details, learned
 * calibration, and the dashboards if wanted -- to another phone, or back to a
 * car driven before, as a file.
 */
import { useMemo, useRef, useState } from 'react';
import {
  describeIdentity, isMismatch,
  type IdentityMatch, type VehicleIdentity, type VehicleProfile,
} from '@obd/core';
import {
  MAX_PROFILE_BYTES, PROFILE_PARTS, checkProfile, describeProfile, parseCarProfile, profileFileName,
  serializeProfile,
  type CarProfile, type ParsedProfile, type ProfilePart,
} from '../store/carProfile';
import { Modal } from './DashboardView';

export interface ProfileActions {
  build: (includeDashboards: boolean) => CarProfile;
  apply: (profile: CarProfile, parts: ReadonlySet<ProfilePart>) => void;
  /** The car connected now, or else the one this device was last connected to. */
  thisCar?: VehicleIdentity;
  /** Whether `thisCar` is connected right now. */
  thisCarLive: boolean;
  /** The simulator is running, so imported calibration waits until it stops. */
  simulating: boolean;
}

type Parsed = Extract<ParsedProfile, { ok: true }>;
type Message = { tone: 'good' | 'warn' | 'error'; text: string };

/** Lower-case names for sentences. */
const PART_NAMES = Object.fromEntries(PROFILE_PARTS.map((p) => [p.part, p.label.toLowerCase()])) as Record<ProfilePart, string>;

export function ProfileCard({ actions, vehicle }: { actions: ProfileActions; vehicle: VehicleProfile }): JSX.Element {
  const [includeDashboards, setIncludeDashboards] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const [pending, setPending] = useState<Parsed | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const shareable = useMemo(canShareFiles, []);
  const where = actions.thisCarLive ? 'the car connected now' : 'the car this phone was last connected to';

  /** A note when the calibration in an outgoing file was learned on another car. */
  const exportNote = (profile: CarProfile): string => {
    const check = checkProfile(profile, actions.thisCar);
    const foreign = PROFILE_PARTS.filter((p) => p.part !== 'vehicle' && isMismatch(check.parts[p.part]));
    if (foreign.length === 0) return '';
    const firstPart = foreign[0]?.part;
    const owner = firstPart === 'gears' || firstPart === 'accelerator' || firstPart === 'speed'
      ? profile.learnedFrom?.[firstPart]
      : undefined;
    return ' Note: the ' + listText(foreign.map((p) => PART_NAMES[p.part])) + ' in it '
      + (foreign.length === 1 ? 'was' : 'were') + ' learned on a different car'
      + (owner ? ' (' + describeIdentity(owner) + ')' : '') + ' than ' + where + ', and the file records that.';
  };

  const exportFile = (): void => {
    const profile = actions.build(includeDashboards);
    const name = profileFileName(profile);
    saveFile(name, serializeProfile(profile), 'application/json');
    const note = exportNote(profile);
    setMessage({
      tone: note ? 'warn' : 'good',
      text: 'Saved ' + name + '. On the other device, open it with “Import profile”.' + note,
    });
  };

  const share = async (): Promise<void> => {
    const profile = actions.build(includeDashboards);
    try {
      await navigator.share({ files: [shareFile(profile)], title: 'Car profile — ' + profile.vehicle.name });
      const note = exportNote(profile);
      setMessage(note ? { tone: 'warn', text: 'Shared.' + note } : null);
    } catch (err) {
      // Closing the share sheet is not a failure.
      if (err instanceof Error && err.name === 'AbortError') return;
      const why = err instanceof Error && err.name ? ' (' + err.name + ')' : '';
      setMessage({ tone: 'error', text: 'Sharing did not work here' + why + '. Use “Export profile” instead.' });
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = e.currentTarget;
    const file = input.files?.[0];
    // Cleared so that choosing the same file again still counts as a change.
    input.value = '';
    if (!file) return;
    if (file.size > MAX_PROFILE_BYTES) {
      setMessage({ tone: 'error', text: 'That file is far too large to be a car profile.' });
      return;
    }
    let text: string;
    try {
      text = await file.text();
    } catch {
      setMessage({ tone: 'error', text: 'That file could not be read.' });
      return;
    }
    const parsed = parseCarProfile(text, vehicle);
    if (parsed.ok) {
      setMessage(null);
      setPending(parsed);
    } else {
      setMessage({ tone: 'error', text: parsed.error });
    }
  };

  const apply = (parts: Set<ProfilePart>): void => {
    if (!pending) return;
    const check = checkProfile(pending.profile, actions.thisCar);
    actions.apply(pending.profile, parts);
    setPending(null);
    const names = PROFILE_PARTS.filter((p) => parts.has(p.part)).map((p) => p.part);
    const foreign = names.filter((p) => p !== 'dashboards' && isMismatch(check.parts[p]));
    const learned = parts.has('gears') || parts.has('accelerator') || parts.has('speed');
    setMessage({
      tone: foreign.length > 0 ? 'warn' : 'good',
      text: 'Imported ' + listText(names.map((p) => PART_NAMES[p])) + '.'
        + (foreign.length > 0
          ? ' The ' + listText(foreign.map((p) => PART_NAMES[p])) + ' came from a different car. While connected to '
            + 'this one, the app keeps reminding you until you keep or re-learn '
            + (foreign.length === 1 ? 'it' : 'them') + ' under “Learned about your car”.'
          : '')
        + (learned && actions.simulating ? ' The calibration takes effect when the simulator stops.' : ''),
    });
  };

  return (
    <div className="card profile-card">
      <h3>Car profile</h3>
      <p className="muted small">
        Everything the app knows about this car in one file: the vehicle details above and what it
        has learned — gear ratios, accelerator calibration and speed correction. Import it on
        another phone, after reinstalling, or when you come back to a car, and it works straight
        away with no re-learning. The file records which car each part was learned on, so an
        import can warn when it comes from a different car.
      </p>

      <p className="small profile-car">
        <span className="muted">This phone’s car: </span>
        {actions.thisCar ? (
          <>
            {describeIdentity(actions.thisCar)}{' '}
            <span className="muted">({actions.thisCarLive ? 'connected now' : 'last connected'})</span>
          </>
        ) : (
          <span className="muted">none yet. Connect once, so imported profiles can be checked against it.</span>
        )}
      </p>

      {message && (
        <p
          className={message.tone === 'error' ? 'error-card' : message.tone === 'warn' ? 'warn-card' : 'small profile-note'}
          role="status"
        >
          {message.text}
        </p>
      )}

      <label className="field-inline">
        <input
          type="checkbox"
          checked={includeDashboards}
          onChange={(e) => setIncludeDashboards(e.target.checked)}
        />
        <span className="muted">Include dashboards</span>
      </label>
      <div className="button-row" style={{ marginTop: 10 }}>
        <button className="btn" data-variant="primary" onClick={exportFile}>Export profile</button>
        {shareable && <button className="btn" onClick={() => void share()}>Share…</button>}
        <button className="btn" onClick={() => fileInput.current?.click()}>Import profile…</button>
        <input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_FILES}
          hidden
          onChange={(e) => void onFile(e)}
        />
      </div>

      {pending && (
        <ImportDialog
          parsed={pending}
          thisCar={actions.thisCar}
          thisCarLive={actions.thisCarLive}
          onApply={apply}
          onClose={() => setPending(null)}
        />
      )}
    </div>
  );
}

function ImportDialog({
  parsed, thisCar, thisCarLive, onApply, onClose,
}: {
  parsed: Parsed;
  thisCar: VehicleIdentity | undefined;
  thisCarLive: boolean;
  onApply: (parts: Set<ProfilePart>) => void;
  onClose: () => void;
}): JSX.Element {
  const { profile, available, warnings } = parsed;
  // Everything the file has is ticked: a different car is a warning, not a veto.
  const [chosen, setChosen] = useState(() => new Set(available));
  const description = useMemo(() => describeProfile(profile), [profile]);
  const check = useMemo(() => checkProfile(profile, thisCar), [profile, thisCar]);
  const saved = new Date(profile.exportedAt);
  const where = thisCarLive ? 'the car connected now' : 'the car this phone was last connected to';

  const foreign = PROFILE_PARTS.filter(
    (p) => p.part !== 'dashboards' && available.includes(p.part) && isMismatch(check.parts[p.part]),
  );
  const carForeign = isMismatch(check.car);
  // Vehicle details are entered, not learned, so they only count as "from another car".
  const learnedForeign = foreign.filter((p) => p.part !== 'vehicle');
  const firstReason = learnedForeign[0] ? check.parts[learnedForeign[0].part]?.reason : undefined;
  const were = (n: number): string => (n === 1 ? 'was' : 'were');
  const unknown = !thisCar
    ? 'This phone has not been connected to a car yet, so it cannot tell whether this profile is from the same one.'
    : !profile.car && !profile.learnedFrom
      ? 'The file does not say which car it came from, so it cannot be checked.'
      : check.car.verdict === 'unknown'
        ? 'It cannot tell whether this is the same car: ' + lowerFirst(check.car.reason)
        : undefined;

  const toggle = (part: ProfilePart, on: boolean): void => {
    setChosen((prev) => {
      const next = new Set(prev);
      if (on) next.add(part);
      else next.delete(part);
      return next;
    });
  };

  return (
    <Modal title="Import car profile" onClose={onClose}>
      <p className="small" style={{ marginTop: 0 }}>
        <strong>{profile.vehicle.name}</strong>
        {saved.getTime() > 0 && (
          // Month as a word: 1/9 means different days to different readers.
          <span className="muted">
            , saved {saved.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
          </span>
        )}
        <SameCarPill match={check.car} />
      </p>
      <div className="kv-grid profile-cars">
        <div className="kv">
          <span className="muted">From</span>
          <span>{profile.car ? describeIdentity(profile.car) : 'not recorded'}</span>
        </div>
        <div className="kv">
          <span className="muted">This phone</span>
          <span>{thisCar ? describeIdentity(thisCar) : 'no car yet'}</span>
        </div>
      </div>

      {foreign.length > 0 && (
        <div className="warn-card" role="alert">
          {carForeign ? (
            <>
              <strong>From a different car.</strong> This profile was saved on a different car than {where}.{' '}
              {check.car.reason}
              {learnedForeign.length > 0 && (
                <>
                  {' '}Its {listText(learnedForeign.map((p) => PART_NAMES[p.part]))} {were(learnedForeign.length)} learned
                  on another car too.
                </>
              )}
            </>
          ) : (
            <>
              <strong>Learned on a different car.</strong> The{' '}
              {listText(learnedForeign.map((p) => PART_NAMES[p.part]))} in this file {were(learnedForeign.length)} learned
              on a different car than {where}. {firstReason}
            </>
          )}{' '}
          You can still import everything: gear ratios usually suit a car of the same model and gearbox, but
          accelerator calibration and speed correction rarely carry over.
        </div>
      )}
      {foreign.length === 0 && unknown && <p className="muted small">{unknown}</p>}

      <div className="profile-parts">
        {PROFILE_PARTS.map(({ part, label, scope }) => {
          const has = available.includes(part);
          const other = has && part !== 'dashboards' && isMismatch(check.parts[part]);
          return (
            <label key={part} className="profile-part" data-disabled={has ? undefined : true}>
              <input
                type="checkbox"
                disabled={!has}
                checked={has && chosen.has(part)}
                onChange={(e) => toggle(part, e.target.checked)}
              />
              <span className="profile-part-text">
                <span className="profile-part-label">
                  {label}
                  {other && <span className="pill" data-tone="warning" style={{ marginLeft: 8 }}>different car</span>}
                </span>
                <span className="small">{has ? description[part] : 'Not in this file.'}</span>
                {has && <span className="muted small">{scope}</span>}
              </span>
            </label>
          );
        })}
      </div>

      {warnings.map((w) => (
        <p key={w} className="muted small">{w}</p>
      ))}
      <p className="muted small">
        Each ticked part replaces what this device has now. To keep a copy of that, export this
        device’s profile first.
      </p>

      <div className="modal-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button
          className="btn"
          data-variant="primary"
          disabled={chosen.size === 0}
          onClick={() => onApply(new Set(chosen))}
        >
          Import
        </button>
      </div>
    </Modal>
  );
}

function SameCarPill({ match }: { match: IdentityMatch }): JSX.Element | null {
  const style = { marginLeft: 8 };
  if (match.verdict === 'same') {
    return <span className="pill" data-tone="good" style={style} title={match.reason}>same car</span>;
  }
  if (match.verdict === 'probably-same') {
    return <span className="pill" data-tone="good" style={style} title={match.reason}>probably the same car</span>;
  }
  if (isMismatch(match)) {
    return <span className="pill" data-tone="warning" style={style} title={match.reason}>different car</span>;
  }
  return null;
}

/** Plain text is on every browser's list of shareable files; JSON is not. */
const ACCEPTED_FILES = 'application/json,.json,text/plain,.txt';

/**
 * The profile as a file the share sheet accepts.
 *
 * Chromium browsers only share files whose type and extension are on a short
 * list -- images, audio, video, PDF and plain text -- and reject anything else
 * with NotAllowedError, even though they report that file sharing works. So
 * the same JSON goes out as a .txt, and import reads either.
 */
export function shareFile(profile: CarProfile): File {
  const name = profileFileName(profile).replace(/\.json$/, '.txt');
  return new File([serializeProfile(profile)], name, { type: 'text/plain' });
}

/** Offer a file for download. */
function saveFile(name: string, text: string, type: string): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked later rather than at once: some browsers start the download asynchronously.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Whether the system share sheet takes a profile. On a phone this is the easy
 * way to get it to another device -- messaging, email, Drive -- and on iOS an
 * installed app cannot always download at all.
 */
function canShareFiles(): boolean {
  try {
    if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
    return navigator.canShare({ files: [new File(['{}'], 'profile.txt', { type: 'text/plain' })] });
  } catch {
    return false;
  }
}

function listText(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
}

const lowerFirst = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);
